'use client'

import { useEffect } from 'react'
import { initRefiner } from './refiner'
import RefinerFeedbackButton from './RefinerFeedbackButton'
import useRefinerIdentified from './useRefinerIdentified'

/**
 * Owns the Refiner (https://refiner.io) SDK lifecycle: init and the feedback
 * tab. Mount this ONLY inside authenticated route-group layouts so it is limited
 * to logged-in experiences and never runs on the sign-up / account-verify screens
 * (which live in the unauthenticated route group).
 *
 * This component does not decide whether Refiner should run: each app's
 * `lib/refiner.server.ts` does, and only hands over a config when it should.
 *
 * Who the user is also stays out of here. Like Appzi before it, each app
 * identifies the user in `OnClientUserLoad` with `identifyRefinerUser`; commands
 * issued before this component has inited simply wait for it (see `refiner.ts`).
 *
 * Audience/copy/trigger rules are configured in the Refiner dashboard and
 * segmented on the traits passed at identify.
 *
 * There is no consent gate: Refiner is in-product feedback for logged-in users,
 * and storage is forced to `localStorage`, so it sets no cookies.
 */

export interface RefinerLoaderProps {
  /** Refiner project ID (NEXT_PUBLIC_REFINER_PROJECT_ID). */
  projectId: string
  /**
   * Refiner survey (Manual trigger) to open from the edge feedback tab. The tab is
   * not rendered when unset, nor until the app has identified the user, since
   * `showForm` needs an identified user.
   */
  feedbackFormId?: string
}

export default function RefinerLoader({ projectId, feedbackFormId }: RefinerLoaderProps) {
  const isIdentified = useRefinerIdentified()

  useEffect(() => {
    initRefiner(projectId)
  }, [projectId])

  if (feedbackFormId && isIdentified) {
    return <RefinerFeedbackButton formId={feedbackFormId} />
  }

  return null
}
